import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';

import { tableStore } from '../../stores';
import type { TableRow } from '../../stores/table-store';
import {
  planVoiceInboxEscalation,
  REWIRE_TO_ENTRY,
  type EscalationRow,
} from '../../stores/utils/voice-inbox-escalation';
import { getChannelsFromPypes } from '../../utils/get-channels-from-pypes';
import { getPypeConnections } from '../../utils/get-pype-connections';
import { checkForErrors } from '../../utils/check-for-errors';
import { toSolutionEnvironment } from '../../utils/map-connection-to-row';
import { planVoiceInboxConnection } from '../../utils/voice-inbox-connection';
import { proStudioService } from '../../xstate/app.xstate';
import { waitForProStudioIdle } from '../../xstate/wait-for-pro-studio-idle';

export interface VoiceInboxSetupResult {
  connection: 'created' | 'moved' | 'unchanged' | 'no-template' | 'no-pype';
  /** `rewired`: already set up, but a stray hand-off (e.g. frustration) was routed in. */
  nodes: 'added' | 'rewired' | 'present' | 'no-entry' | 'not-loaded';
}

/**
 * Everything after the channel save that a Contact Center voice inbox needs,
 * besides the inbox itself (made in Contact Center, App ID = the channel's
 * integrator id):
 *   1. the voice pype on a connection of its own for that inbox
 *      (`planVoiceInboxConnection` says why);
 *   2. the solution's escalation sending `handoff_type: voice_inbox`
 *      (`planVoiceInboxEscalation`), saved to the draft — it goes live on the
 *      next publish.
 *
 * Runs once the save has settled, so the channel (and, for a new one, its pype)
 * is in the machine's context.
 */
export const setUpVoiceInbox = async ({
  projectId,
  channelId,
  channelName,
  env,
  accountId,
  inboxId,
}: {
  projectId: string;
  /** The voice channel's stream id; absent for a channel just created. */
  channelId?: string;
  channelName: string;
  env: 'live' | 'sandbox';
  accountId: string;
  inboxId: string;
}): Promise<VoiceInboxSetupResult> => {
  await waitForProStudioIdle(proStudioService);
  const channels = getChannelsFromPypes(
    proStudioService.getSnapshot().context.botPypes ?? []
  ).filter((c) => c.type === 'twilio_voice' && c.env === env);
  const channel = channelId
    ? channels.find((c) => c.id === channelId)
    : channels.find((c) => c.name === channelName);

  const connection = channel?.pypeId
    ? await bindToInboxConnection({
        projectId,
        pypeId: channel.pypeId,
        env,
        accountId,
        inboxId,
      })
    : 'no-pype';

  return { connection, nodes: addEscalationNodes() };
};

const bindToInboxConnection = async ({
  projectId,
  pypeId,
  env,
  accountId,
  inboxId,
}: {
  projectId: string;
  pypeId: string;
  env: 'live' | 'sandbox';
  accountId: string;
  inboxId: string;
}): Promise<VoiceInboxSetupResult['connection']> => {
  const solutionEnv = toSolutionEnvironment(env);
  const plan = planVoiceInboxConnection(
    await getPypeConnections({ projectId }),
    { pypeId, env: solutionEnv, accountId, inboxId }
  );
  if (plan.kind === 'no-template') return 'no-template';
  if (plan.done) return 'unchanged';

  let { connectionId } = plan;
  if (!connectionId && plan.create) {
    const created = await urqlGql.createPypeConnection({
      projectId,
      name: plan.create.name,
      platformId: plan.create.platformId,
      environment: solutionEnv,
      settings: plan.create.settings,
      agentRouting: plan.create.agentRouting,
      // See create-live-agent-platform.ts: an explicit null must reach the wire.
      agentRoutingUrl: plan.create.agentRoutingUrl as string | undefined,
    });
    checkForErrors(created);
    connectionId = created.data?.admin_?.createPypeConnection?.id ?? null;
    if (!connectionId) throw new Error('Contact Center connection not created');
  }

  for (const id of plan.detachFrom) {
    checkForErrors(
      await urqlGql.removePypeFromPypeConnection({
        id,
        pypeId,
        env: solutionEnv,
      })
    );
  }
  checkForErrors(
    await urqlGql.addPypeToPypeConnection({
      id: connectionId!,
      pypeId,
      env: solutionEnv,
    })
  );
  return plan.connectionId ? 'moved' : 'created';
};

const addEscalationNodes = (): VoiceInboxSetupResult['nodes'] => {
  if (!tableStore.rows.length) return 'not-loaded';
  const plan = planVoiceInboxEscalation(
    tableStore.rows as unknown as EscalationRow[]
  );
  if (plan.kind === 'no-entry') return plan.kind;

  plan.rewire.forEach((rowId) =>
    tableStore.updateRow({
      rowId,
      updates: REWIRE_TO_ENTRY as unknown as Partial<TableRow>,
      // Without it updateRow leaves the save to a cell's blur, which never comes.
      saveImmediately: true,
    })
  );
  if (plan.kind === 'present') {
    return plan.rewire.length ? 'rewired' : 'present';
  }

  const sendId = tableStore.insertRowBelow(
    plan.entryRowId,
    plan.send as Partial<TableRow>
  );
  tableStore.insertRowBelow(sendId, plan.transfer as Partial<TableRow>);
  tableStore.updateRow({
    rowId: plan.entryRowId,
    updates: plan.entry as Partial<TableRow>,
    saveImmediately: true,
  });
  return 'added';
};
